import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { AnimatePresence } from 'framer-motion';
import { Draggable } from 'gsap/dist/Draggable';
import { gsap } from 'gsap';

import { ReactComponent as PictureIcon } from '../GridItem/Link/images/picture.svg';
import { IconWrapper, StyledExpandIcon } from '../GridItem/NFTPreviewButton/styles';
import {
    InnerWrapper,
    OuterWrapper,
    ImageSwitchWrapper,
    StyledSwitch,
    ActionsWrapper,
    ContentWrapper,
    StyledButton,
    DisplayAttributesWrapper,
    DraggableBar,
    ItemEditMobileOverlay,
    NFTPreviewWrapper,
} from './styles';
import LinkAttribute from './LinkAttribute';
import { AttributeWrapper } from './LinkAttribute/styles';
import LinkAttributeInput, { type EditingAttribute } from './LinkAttributeInput';
import { LinkContent, ContentItem, ContentType, NFTContent } from '@/store/types/yat-page-actions';
import { editItem, removeItem, saveModifiedPageItems, setEditingItemId } from '@/store/actions/yat-page-item-actions';
import { previewYatPageItem } from '@/store/slices';
import { useMediaQuery } from '@/providers';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { Typography } from '@/components/DesignSystem';
import { ReactComponent as DeleteIcon } from '@/components/Sections/Profile/icons/trash-bin.svg';

gsap.registerPlugin(Draggable);

interface Props {
    editMode?: boolean;
}

const ItemEditMobile = ({ editMode }: Props) => {
    const navigate = useNavigate();
    const { t } = useTranslation('yat_page');
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const { usingMouse } = useMediaQuery();
    const editingItemId = useAppSelector((state) => state.yatPage.editingItemId);
    const pageAssets = useAppSelector((state) => state.yatPage.pageAssets);
    const inPage = useAppSelector((state) => state.yatPage.pageAssets.sorted);

    const outerWrapperRef = useRef<HTMLDivElement>(null);
    const innerWrapperRef = useRef<HTMLDivElement>(null);
    const spacerRef = useRef<HTMLDivElement>(null);
    const [editingAttribute, setEditingAttribute] = useState<EditingAttribute>();
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [linkItem, setLinkItem] = useState<ContentItem<LinkContent>>();
    const [nftItem, setNftItem] = useState<ContentItem<NFTContent>>();
    const addedToYatPage = !!nftItem && inPage.some((currentItem) => currentItem === nftItem.id);

    let placeholderText;
    if (editingAttribute)
        placeholderText = {
            title: t('add_card_title_placeholder'),
            description: t('add_card_description_placeholder'),
            url: t('add_card_url_placeholder'),
        }[editingAttribute];

    useEffect(() => {
        if (editingItemId) {
            const editingItem = pageAssets.draft[editingItemId]?.asset;
            if (editingItem?.type == ContentType.LINK) {
                const linkItem = editingItem as ContentItem<LinkContent>;
                setLinkItem(linkItem);
                if (!linkItem.content?.url) {
                    setEditingAttribute('url');
                }
            } else if (editingItem?.type == ContentType.NFT) {
                setNftItem(editingItem as ContentItem<NFTContent>);
            }
        }
    }, [editingItemId, pageAssets.draft]);

    const updateCollapsed = useCallback(() => {
        if (linkItem && linkItem.content) {
            dispatch(
                editItem({
                    ...linkItem,
                    collapsed: !linkItem.collapsed,
                    content: {
                        ...linkItem.content,
                    },
                }),
            );
        }
    }, [dispatch, linkItem]);

    // Create overlay spacer so that any content at bottom of screen is pushed up and not hidden by overlay
    useEffect(() => {
        if (!spacerRef.current || !outerWrapperRef.current || !linkItem) return;

        gsap.set(spacerRef.current, { height: outerWrapperRef.current.clientHeight });
    }, [linkItem]);

    const handleDelete = () => {
        if (!confirmDelete) {
            setConfirmDelete(true);
            return;
        }
        const item = linkItem || nftItem;
        item && dispatch(removeItem(item.id));
        dispatch(setEditingItemId());
        setConfirmDelete(false);
        close();
    };

    const close = () => {
        dispatch(setEditingItemId());
        setLinkItem(undefined);
        setNftItem(undefined);
        setEditingAttribute(undefined);
    };

    const handleSave = () => {
        if (confirmDelete) return setConfirmDelete(false);
        close();
        if (linkItem?.id && !linkItem?.content?.url) {
            dispatch(saveModifiedPageItems());
        }
    };

    const handlePointerDown = (event: React.MouseEvent) => {
        if (!outerWrapperRef.current) return;

        const wrapperHeight = outerWrapperRef.current.offsetHeight;

        const handleDragEnd = (draggable: Draggable) => {
            if (draggable.endY > wrapperHeight * 0.2) close();
            else {
                gsap.to(outerWrapperRef.current, {
                    x: 0,
                    y: 0,
                    duration: theme.transitions.speeds.normal,
                    ease: 'power2.out',
                });
            }
            draggable.kill();
        };

        const draggable = new Draggable(outerWrapperRef.current, {
            zIndexBoost: false,
            type: 'y',
            liveSnap: {
                y: (y) => {
                    return y < 0 ? 0 : y > wrapperHeight ? wrapperHeight : y;
                },
            },
            onDragEnd: () => handleDragEnd(draggable),
        });

        draggable.startDrag(event.nativeEvent);
    };

    const sharedTransition = {
        duration: theme.transitions.speeds.normal,
        ease: theme.transitions.eases.cubic,
    };

    if (!editMode) return null;

    return (
        <AnimatePresence>
            {(linkItem || nftItem) && !usingMouse && (
                <>
                    <div ref={spacerRef} />

                    <ItemEditMobileOverlay onClick={handleSave} />

                    <OuterWrapper ref={outerWrapperRef} $forceBottom>
                        <InnerWrapper
                            ref={innerWrapperRef}
                            initial={{ y: '100%' }}
                            animate={{ y: 0 }}
                            exit={{ y: '100%' }}
                            transition={sharedTransition}
                        >
                            <DraggableBar onPointerDown={handlePointerDown} />
                            <AnimatePresence mode="wait" initial={false}>
                                {editingAttribute && linkItem ? (
                                    <ContentWrapper
                                        key="mobile-edit-input"
                                        //initial={{ x: '100%' }}
                                        //animate={{ x: 0 }}
                                        exit={{ x: '100%' }}
                                        transition={sharedTransition}
                                    >
                                        <LinkAttributeInput
                                            hasBackButton={editingAttribute !== 'url' || !!linkItem.content?.url}
                                            linkItem={linkItem}
                                            editingAttribute={editingAttribute}
                                            onClose={() => setEditingAttribute(undefined)}
                                            placeholderText={placeholderText}
                                        />
                                    </ContentWrapper>
                                ) : (
                                    <ContentWrapper
                                        key="mobile-edit-drawer"
                                        initial={{ x: '-100%' }}
                                        animate={{ x: 0 }}
                                        exit={{ x: '-100%' }}
                                        transition={sharedTransition}
                                    >
                                        {linkItem ? (
                                            <>
                                                <DisplayAttributesWrapper $confirmDelete={confirmDelete}>
                                                    {linkItem.content?.image && (
                                                        <AttributeWrapper>
                                                            <Typography type="label" size={16} weight="600">
                                                                {t('image')}
                                                            </Typography>
                                                            <ImageSwitchWrapper
                                                                $active={!linkItem?.collapsed}
                                                                onClick={() => updateCollapsed()}
                                                            >
                                                                <StyledSwitch>
                                                                    <PictureIcon width={15} />
                                                                </StyledSwitch>
                                                            </ImageSwitchWrapper>
                                                        </AttributeWrapper>
                                                    )}

                                                    <LinkAttribute
                                                        onClick={() => setEditingAttribute('title')}
                                                        text={
                                                            linkItem.content?.title || t('add_card_title_placeholder')
                                                        }
                                                        weight="700"
                                                    />
                                                    <LinkAttribute
                                                        onClick={() => setEditingAttribute('description')}
                                                        color="#ffffff80"
                                                        text={
                                                            linkItem.content?.description ||
                                                            t('add_card_description_placeholder')
                                                        }
                                                        weight="400"
                                                    />
                                                    <LinkAttribute
                                                        onClick={() => setEditingAttribute('url')}
                                                        text={linkItem.content?.url || t('add_card_url_placeholder')}
                                                        weight="400"
                                                    />
                                                </DisplayAttributesWrapper>

                                                <ActionsWrapper>
                                                    <StyledButton
                                                        color={confirmDelete ? 'error' : 'secondary'}
                                                        onClick={handleDelete}
                                                        fluid
                                                        variant="solid"
                                                        startIcon={<DeleteIcon />}
                                                    >
                                                        {confirmDelete ? t('yes_delete') : t('delete')}
                                                    </StyledButton>
                                                    <StyledButton fluid onClick={handleSave}>
                                                        {confirmDelete ? t('cancel') : t('save')}
                                                    </StyledButton>
                                                </ActionsWrapper>
                                            </>
                                        ) : (
                                            <>
                                                <NFTPreviewWrapper
                                                    onClick={() =>
                                                        dispatch(previewYatPageItem(nftItem, navigate, addedToYatPage))
                                                    }
                                                >
                                                    <Typography type="label" size={16} weight="700">
                                                        {t('nft_preview_mobile')}
                                                    </Typography>
                                                    <IconWrapper>
                                                        <StyledExpandIcon width={10} height={10} />
                                                    </IconWrapper>
                                                </NFTPreviewWrapper>
                                                <ActionsWrapper>
                                                    <StyledButton
                                                        color={confirmDelete ? 'error' : 'secondary'}
                                                        onClick={handleDelete}
                                                        startIcon={<DeleteIcon />}
                                                        fluid
                                                    >
                                                        {confirmDelete ? t('yes_delete') : t('delete')}
                                                    </StyledButton>
                                                </ActionsWrapper>
                                            </>
                                        )}
                                    </ContentWrapper>
                                )}
                            </AnimatePresence>
                        </InnerWrapper>
                    </OuterWrapper>
                </>
            )}
        </AnimatePresence>
    );
};

export default ItemEditMobile;
